import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import { nest } from 'd3-collection';
import { accessibility } from 'accessible-react-table';

import treeTableHOC from './treeTableHOC';
import DangerousWrapper from '../DangerousWrapper';
import { getColumns } from '../../utils/table';
import './index.scss';

class ExpTreeTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: this.expandAll()
    };
  }

  componentDidUpdate(prevProps) {
    const { expandLevel } = this.props;
    if (prevProps.expandLevel !== expandLevel) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ expanded: this.expandAll() });
    }
  }

  onExpandedChange = (expanded) => {
    this.setState({ expanded });
  };

  expandIt = ({ data, remaining }) => {
    const expandThese = {};
    Object.keys(data)
      .filter((value) => !Number.isNaN(value))
      .forEach((rowIndex) => {
        if (remaining > 0 && data[rowIndex].values) {
          expandThese[rowIndex] = this.expandIt({
            data: data[rowIndex].values,
            remaining: remaining - 1
          });
        } else {
          expandThese[rowIndex] = {};
        }
      });
    return expandThese;
  };

  expandAll() {
    const { data, pivotBy, expandLevel } = this.props;
    const myNest = nest();
    pivotBy.forEach((pivot) => {
      myNest.key((d) => d[pivot]);
    });
    return this.expandIt({
      data: myNest.entries(data),
      remaining: expandLevel
    });
  }

  render() {
    const TreeTable = accessibility(treeTableHOC(ReactTable));
    const {
      table,
      data,
      pivotBy,
      aggregateBy,
      subComponent,
      subIndex,
      maxColWidth,
      setMaxColWidth = Object.prototype.hasOwnProperty.call(this.props, 'maxColWidth'),
      mantissa
    } = this.props;
    const { expanded } = this.state;
    /** Creates a custom expander column for the table that renders
        a custom view/hide details button component */
    const expander = {
      expander: true,
      Header: '',
      width: 85,
      Expander: ({ isExpanded, index }) => (
        <div>
          { isExpanded
            ? (
              <button
                type="button"
                className="ma__expandable-section open"
                aria-expanded="true"
                aria-controls={`${subComponent}-${index}`}
              >
                  hide details
              </button>
            ) : (
              <button
                type="button"
                className="ma__expandable-section closed"
                aria-expanded="false"
                aria-controls={`${subComponent}-${index}`}
              >
                  view details
              </button>
            )
          }
        </div>
      ),
      style: {
        cursor: 'pointer',
        padding: '0',
        textAlign: 'left',
        userSelect: 'none'
      }
    };
    const columns = getColumns(table, {
      aggregateBy, setMaxColWidth, maxColWidth, mantissa
    });
    /** Inserts the expander column into the columns object
        at the index specified by this.props.subIndex */
    if (subIndex) {
      columns.splice(subIndex, 0, expander);
    }
    const tableProps = {
      columns,
      data,
      showPaginationBottom: false,
      className: `ma__tree-table ma__tree-table--${pivotBy.length}`,
      minRows: 0,
      pivotBy,
      aggregateBy,
      /** As of now, ExpTreeTable is not sortable due to the custom expanded object
          functionality and the fact that view row indices are changed on sort. */
      sortable: false,
      expanded,
      onExpandedChange: this.onExpandedChange
    };
    if (subComponent) {
      tableProps.SubComponent = (row) => {
        if (row.original[subComponent]) {
          return(
            <div className="rt-tr">
              <div className="rt-td">
                <DangerousWrapper
                  id={`${subComponent}-${row.index}`}
                  className="string nested"
                  rawHtml={row.original[subComponent]}
                />
              </div>
            </div>
          );
        } return null;
      };
    }
    return(
      <TreeTable {...tableProps} />
    );
  }
}

ExpTreeTable.propTypes = {
  /** The table data, headers, and total to be rendered by ExpTreeTable. */
  table: PropTypes.object,
  /** The table data object. */
  data: PropTypes.array,
  /** An array of fields (accessors) in the dataset that you want to pivot on
      The ordering of the values in the array will match the pivot order */
  pivotBy: PropTypes.array,
  /** An array of fields (accessors) that should be displayed and aggregrated at the pivot levels.
      Any values passed in the array will be displayed in the pivot rows of ExpTreeTable */
  aggregateBy: PropTypes.array,
  /** The value you want displayed as a nested subcomponent of a row with expand/hide functionality. */
  subComponent: PropTypes.string,
  /** The index of the column where you want to insert the subcomponent view and hide details button. */
  subIndex: PropTypes.number,
  /** How many levels of the ExpTreeTable you would like to be expanded, starting a level 0. */
  expandLevel: PropTypes.number,
  /** The max col width in pixels. */
  maxColWidth: PropTypes.number,
  /** Whether to set the max columns width or not. */
  setMaxColWidth: PropTypes.bool,
  /** The number of decimal place for numbers and currency cells/columns. */
  mantissa: PropTypes.number

};

export default ExpTreeTable;
